
import { useEffect, useRef } from 'react';
import request from 'utils/request';
import {
  getAuthtoken,
  getSelectedEntityId,
  getSelectedOrganizationId,
  setAuthToken,
  setSelectedOrganizationId,
  useAuthenticated,
  useAuthtoken,
  useSelectedOrganizationId,
} from 'stores/useAuthStore';
import {
  resetOrganizations,
  useOrganizationsList,
} from 'stores/useOrganizationsStore';
import { useServiceBusStore } from 'stores/useServiceBusStore';
import { useUiStore } from 'stores/useUiStore';
import { resetUser } from 'stores/useSessionStore';
import { queryClient } from 'providers/QueryProvider';
import { resetTenantScopedStores } from './useTenantScopeReset';


export async function refreshAuthToken() {
  try {
    const authtoken = getAuthtoken();
    const result = await request<{ success: boolean; token: string }>(
      `/api/users/refreshToken`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          authorization: authtoken,
        },
      }
    );
    if (result?.success) {
      setAuthToken(result.token);
    }
  } catch (error) {
    console.error('Error refreshing auth token:', error);
  }
}

export function useAppRealtime() {
  const authenticated = useAuthenticated();
  const authtoken = useAuthtoken();
  const selectedOrganizationId = useSelectedOrganizationId();
  const organizations = useOrganizationsList();

  useEffect(() => {
    if (!organizations?.lastUpdated) return;
    const selectedEntityId = getSelectedEntityId();
    const currentOrganizationId = getSelectedOrganizationId();
    const orgSetupWizard = useUiStore.getState().orgSetupWizard;

    if (orgSetupWizard?.open && orgSetupWizard.organizationId != null) return;

    const firstOrg = organizations.values.find(
      (o) => o.entityId === selectedEntityId
    );
    const org = organizations.values.find(
      (o) =>
        o.entityId === selectedEntityId &&
        o.organizationId === currentOrganizationId
    );

    if (!org && firstOrg) {
      if (firstOrg.organizationId !== currentOrganizationId) {
        setSelectedOrganizationId(firstOrg.organizationId);
      }
    } else if (org) {
      if (org.organizationId !== currentOrganizationId) {
        setSelectedOrganizationId(org.organizationId);
      }
    } else if (!org && !firstOrg) {
      setSelectedOrganizationId(NaN);
    }
  }, [organizations]);

  const handledRealtimeScopeRef = useRef<{
    token: string;
    entityId: number;
    organizationId: number;
  } | null>(null);
  useEffect(() => {
    if (!authtoken) {
      handledRealtimeScopeRef.current = null;
      return;
    }
    const selectedEntityId = getSelectedEntityId();
    const currentOrganizationId = selectedOrganizationId;
    const org = organizations?.values.find(
      (o) =>
        o.entityId === selectedEntityId &&
        o.organizationId === currentOrganizationId
    );
    if (!org) return;
    const previousScope = handledRealtimeScopeRef.current;
    if (
      previousScope?.token === authtoken &&
      previousScope.entityId === selectedEntityId &&
      previousScope.organizationId === currentOrganizationId
    ) {
      return;
    }
    const tenantChanged =
      previousScope &&
      (previousScope.entityId !== selectedEntityId ||
        previousScope.organizationId !== currentOrganizationId);
    if (tenantChanged && previousScope.token === authtoken) return;
    handledRealtimeScopeRef.current = {
      token: authtoken,
      entityId: selectedEntityId,
      organizationId: currentOrganizationId,
    };

    const sb = useServiceBusStore.getState();
    if (!sb.subscription || tenantChanged) {
      if (tenantChanged) sb.reset();
      sb.provisionSubscription();
    } else {
      sb.refreshOnlineStatus();
    }
  }, [authtoken, organizations, selectedOrganizationId]);

  const telemetrySentRef = useRef(false);
  useEffect(() => {
    if (!authenticated) {
      telemetrySentRef.current = false;
      return;
    }
    if (
      telemetrySentRef.current ||
      !authtoken ||
      !organizations.lastUpdated ||
      !Number.isFinite(selectedOrganizationId)
    ) {
      return;
    }
    telemetrySentRef.current = true;
    request(`/api/telemetry`, {
      method: 'POST',
      body: JSON.stringify({ telemetryDescription: 'Portal Login' }),
      headers: {
        'Content-Type': 'application/json',
        authorization: authtoken,
      },
    }).catch((error) => console.error('Error recording login telemetry:', error));
  }, [
    authenticated,
    authtoken,
    organizations.lastUpdated,
    selectedOrganizationId,
  ]);

  const prevAuthRef = useRef(authenticated);
  useEffect(() => {
    const wasAuthenticated = prevAuthRef.current;
    prevAuthRef.current = authenticated;
    if (wasAuthenticated && !authenticated) {
      sessionStorage.clear();
      queryClient.clear();
      resetTenantScopedStores();
      resetOrganizations();
      useServiceBusStore.getState().reset();
      useUiStore.getState().resetUiOnLogout();
      resetUser();
    }
  }, [authenticated]);
}
